// src/components/workflows/nodes/UpdateFieldNode.jsx - Update Field Action Node
import React, { useState } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';

import { Badge } from '../../ui/badge.tsx';
import { Input } from '../../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select.tsx';
import { Button } from '../../ui/button.tsx';
import { Edit, Plus, X, Edit2, Flag } from 'lucide-react';

/**
 * UpdateFieldNode - Represents a customer field update action
 *
 * Node data structure (BACKEND-ALIGNED):
 * {
 *   id: 'update-uuid',
 *   type: 'action',                  // ← Changed from 'updateField'
 *   data: {
 *     actionType: 'update_customer', // ← Backend checks this
 *     config: {                      // ← Backend expects config object
 *       updates: {
 *         'is_vip': true,
 *         'status': 'active'
 *       }
 *     },
 *     label: 'Mark as VIP',
 *     description: 'Mark customer as VIP'
 *   }
 * }
 */
const UpdateFieldNode = ({ data, selected, id }) => {
  const {
    actionType = 'update_customer',
    config = {},
    label,
    description,
    isStageGate = false,
    stageName = '',
    stageColor = 'orange'
  } = data;
  // Convert config.updates object to array for UI
  const updates = config.updates ? Object.entries(config.updates).map(([field, value]) => ({ field, value })) : [];
  const { updateNodeData } = useReactFlow();

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(description || '');
  const [isAddingUpdate, setIsAddingUpdate] = useState(false);
  const [newUpdate, setNewUpdate] = useState({ field: '', value: '' });

  // Save description
  const handleSaveDescription = () => {
    updateNodeData(id, { ...data, description: editedDescription.trim() });
    setIsEditingDescription(false);
  };

  // Add field update
  const handleAddUpdate = () => {
    if (newUpdate.field && newUpdate.value) {
      // Convert array back to object for backend
      const updatesObject = { ...(config.updates || {}) };
      updatesObject[newUpdate.field] = newUpdate.value;

      updateNodeData(id, {
        ...data,
        config: { ...config, updates: updatesObject }
      });
      setNewUpdate({ field: '', value: '' });
      setIsAddingUpdate(false);
    }
  };

  // Remove field update
  const handleRemoveUpdate = (index) => {
    const fieldToRemove = updates[index].field;
    // Convert array to object, excluding the removed field
    const updatesObject = { ...(config.updates || {}) };
    delete updatesObject[fieldToRemove];

    updateNodeData(id, {
      ...data,
      config: { ...config, updates: updatesObject }
    });
  };

  return (
    <>
      {/* Input handle (left side) */}
      <Handle isConnectable={1}
        type="target"
        position={Position.Left}
        id="in"
        style={{ left: '-8px', zIndex: 1000 }}
        className="w-3 h-3 !bg-orange-500"
      />

      <div className={`
        w-64 rounded-lg border bg-slate-900
        ${selected ? 'ring-2 ring-orange-500 shadow-lg border-orange-500/50' : 'shadow-md border-orange-500/20'}
        transition-all duration-200 hover:shadow-xl
        bg-gradient-to-br from-orange-500/10 to-orange-600/5
        relative
      `}>
        {/* Stage gate indicator flag */}
        {isStageGate && (
          <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center shadow-lg z-10 border-2 border-slate-950">
            <Flag className="w-3.5 h-3.5 text-white" />
          </div>
        )}

        {/* Header */}
        <div className="p-3 pb-2 border-b border-orange-500/10">
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold text-orange-100 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
                <Edit className="w-4 h-4 text-white" />
              </div>
              Update Field
            </div>
            <Badge variant="outline" className="text-xs border-orange-500/50 text-orange-300">
              {updates.length}
            </Badge>
          </div>
        </div>

        {/* Content */}
        <div className="p-3 space-y-2">
          {/* Updates List */}
          <div className="space-y-1">
            {updates.map((update, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-xs bg-slate-800/50 rounded px-2 py-1.5 border border-slate-700/50 nodrag"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-slate-300 font-mono truncate">
                    {update.field}
                  </div>
                  <div className="text-orange-300 font-medium truncate">
                    → {String(update.value)}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRemoveUpdate(index)}
                  className="h-4 w-4 p-0 ml-2 text-red-400 hover:text-red-300 flex-shrink-0"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>

          {/* Add Update */}
          {isAddingUpdate ? (
            <div className="space-y-2 p-2 bg-slate-800/30 rounded border border-slate-700/50 nodrag">
              <Input
                placeholder="Field name..."
                value={newUpdate.field}
                onChange={(e) => setNewUpdate({ ...newUpdate, field: e.target.value })}
                className="h-7 text-xs bg-slate-800 border-slate-700 text-slate-200"
              />
              <Input
                placeholder="New value..."
                value={newUpdate.value}
                onChange={(e) => setNewUpdate({ ...newUpdate, value: e.target.value })}
                className="h-7 text-xs bg-slate-800 border-slate-700 text-slate-200"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleAddUpdate}
                  className="flex-1 h-6 text-xs bg-green-600 hover:bg-green-700"
                >
                  Add
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsAddingUpdate(false);
                    setNewUpdate({ field: '', value: '' });
                  }}
                  className="flex-1 h-6 text-xs"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsAddingUpdate(true)}
              className="w-full h-7 text-xs border-orange-500/30 text-orange-300 hover:bg-orange-500/10 nodrag"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Field Update
            </Button>
          )}

          {/* Empty State */}
          {updates.length === 0 && !isAddingUpdate && (
            <div className="text-xs text-slate-500 italic text-center py-2">
              No field updates configured
            </div>
          )}

          {/* Editable Description */}
          <div className="border-t border-orange-500/10 pt-2">
            {isEditingDescription ? (
              <Input
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                onBlur={handleSaveDescription}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveDescription();
                  if (e.key === 'Escape') {
                    setEditedDescription(description || '');
                    setIsEditingDescription(false);
                  }
                }}
                placeholder="Add description..."
                autoFocus
                className="text-xs bg-slate-800 border-slate-700 text-slate-200 nodrag"
              />
            ) : (
              <div
                className="flex items-center gap-2 justify-center cursor-pointer nodrag"
                onClick={() => {
                  setEditedDescription(description || '');
                  setIsEditingDescription(true);
                }}
              >
                {description ? (
                  <div className="text-xs text-slate-400 italic text-center">
                    {description}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 italic">
                    Add description...
                  </div>
                )}
                <Edit2 className="w-3 h-3 text-slate-500 hover:text-orange-400" />
              </div>
            )}
          </div>

          {/* Stage Gate Toggle Section */}
          <div className="border-t border-orange-500/10 pt-3">
            <label className="flex items-center gap-2 cursor-pointer nodrag">
              <input
                type="checkbox"
                checked={isStageGate}
                onChange={(e) => updateNodeData(id, {
                  ...data,
                  isStageGate: e.target.checked,
                  stageName: e.target.checked && !stageName ? 'New Stage' : stageName
                })}
                className="w-4 h-4 text-purple-500 bg-slate-800 border-slate-600 rounded focus:ring-purple-500"
              />
              <span className="text-xs text-slate-300 font-medium">Make this a stage gate</span>
            </label>

            {isStageGate && (
              <div className="mt-2 space-y-2 nodrag">
                <Input
                  placeholder="Stage name (e.g., 'Field Updated')"
                  value={stageName}
                  onChange={(e) => updateNodeData(id, {
                    ...data,
                    stageName: e.target.value
                  })}
                  className="text-xs bg-slate-800/50 border-slate-700 text-slate-200"
                />
                <Select
                  value={stageColor}
                  onValueChange={(color) => updateNodeData(id, {
                    ...data,
                    stageColor: color
                  })}
                >
                  <SelectTrigger className="w-full bg-slate-800/50 border-slate-700 text-slate-200 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="purple" className="text-slate-200">Purple</SelectItem>
                    <SelectItem value="blue" className="text-slate-200">Blue</SelectItem>
                    <SelectItem value="green" className="text-slate-200">Green</SelectItem>
                    <SelectItem value="orange" className="text-slate-200">Orange</SelectItem>
                    <SelectItem value="red" className="text-slate-200">Red</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Output handle (right side) */}
      <Handle isConnectable={1}
        type="source"
        position={Position.Right}
        id="out"
        style={{ right: '-8px', zIndex: 1000 }}
        className="w-3 h-3 !bg-orange-500"
      />
    </>
  );
};

export default UpdateFieldNode;
